import { useMemo } from 'react'

import { RESOURCE_STATIC_FIELDS } from '~/client/src/shared/constants/resourceTypes'
import { buildResourceListProperties } from '~/client/src/shared/resources/builders/buildResourceListProperties'
import { useResourceFieldCatalog } from '~/client/src/shared/resources/catalog/useResourceFieldCatalog'
import { IResourceRuntimeContext } from '~/client/src/shared/resources/definitions'
import { ResourcePropertyDto } from '~/client/src/shared/resources/domain/resourceSchema'
import { useResourceFieldProjector } from '~/client/src/shared/resources/projection/useResourceFieldProjector'
import { UNASSIGNED_FILTER_VALUE } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import { IResourceColumn } from '../types'
import {
  isVisibleResourceColumnField,
  shouldCreateHiddenBehaviorColumn,
} from './resourceColumnVisibility'

interface UseDatabaseViewParams {
  resourceTypeId?: string
  resourceRuntimeContext: IResourceRuntimeContext
  properties: ResourcePropertyDto[]
  loading: boolean
}

interface UseDatabaseViewResult {
  columns: IResourceColumn[]
  behaviorColumns: IResourceColumn[]
  loading: boolean
  fieldCatalog: ReturnType<typeof useResourceFieldCatalog>
  fieldProjector: ReturnType<typeof useResourceFieldProjector>
}

export const useDatabaseView = ({
  resourceTypeId,
  resourceRuntimeContext,
  properties: propertySource,
  loading,
}: UseDatabaseViewParams): UseDatabaseViewResult => {
  const properties = useMemo(
    () => buildResourceListProperties(resourceTypeId, propertySource),
    [propertySource, resourceTypeId],
  )

  const fieldCatalog = useResourceFieldCatalog({
    resourceTypeId,
    properties,
  })
  const fieldProjector = useResourceFieldProjector({
    catalog: fieldCatalog,
    resourceTypeId,
    runtimeContext: resourceRuntimeContext,
  })

  const { columns, behaviorColumns } = useMemo((): {
    columns: IResourceColumn[]
    behaviorColumns: IResourceColumn[]
  } => {
    const allColumns: IResourceColumn[] = [
      {
        label: '',
        dataKey: 'checkbox',
        width: 48,
        accessorFn: row => row.id,
        isFacetable: false,
        isSearchable: false,
      },
    ]

    const staticFieldsByKey = new Map(
      (resourceTypeId ? RESOURCE_STATIC_FIELDS[resourceTypeId] || [] : []).map(
        field => [field.name, field],
      ),
    )

    const fieldColumns: IResourceColumn[] = []
    const hiddenBehaviorColumns: IResourceColumn[] = []
    const visibleFieldKeys = new Set(properties.map(property => property.name))

    fieldCatalog.fields.forEach(field => {
      const propertyType = field.propertyType
      const propertyConfig = field.configuration
      const propertyName = field.key
      const isStaticField = field.source === 'static'
      const isFacetable = field.capabilities.filterable

      const displayFormatter = (value: any) =>
        fieldProjector.fromValue(propertyName, value).displayText
      const shouldFormatInCell =
        propertyConfig?.unit !== 'minutes' &&
        propertyConfig?.unit !== 'hours' &&
        propertyType !== 'select' &&
        propertyType !== 'hierarchy' &&
        propertyType !== 'checkbox' &&
        propertyType !== 'user' &&
        propertyType !== 'company' &&
        propertyType !== 'file'

      const hierarchy = field.hierarchy

      const column = {
        label: field.label,
        dataKey: isStaticField ? propertyName : `data.${propertyName}`,
        width:
          staticFieldsByKey.get(propertyName)?.width ||
          // Icon glyph / color swatch cells need only a narrow column.
          (field.cellType ? 100 : 250),
        propertyType,
        accessorFn: row => fieldProjector.resolve(row, propertyName).rawValue,
        facetAccessorFn: isFacetable
          ? (row: any) => {
              const entries = fieldProjector.resolve(
                row,
                propertyName,
              ).filterEntries
              const optionIds = entries.map(entry => entry.id)
              if (optionIds.length === 0) return undefined
              return optionIds.length === 1 ? optionIds[0] : optionIds
            }
          : undefined,
        facetDisplayFormatter: isFacetable
          ? (value: any) =>
              fieldProjector.fromEntryId(
                propertyName,
                value == null ? UNASSIGNED_FILTER_VALUE : String(value),
              ).label
          : undefined,
        searchAccessorFn: (row: any) =>
          fieldProjector.resolve(row, propertyName).searchText,
        displayFormatter,
        cellFormatter: shouldFormatInCell ? displayFormatter : undefined,
        propertyConfig: {
          ...propertyConfig,
          propertyType,
        },
        hierarchy,
        isFacetable,
        facetLabel: field.label,
        isSearchable: field.capabilities.searchable,
        cellType: field.cellType,
      }

      if (isVisibleResourceColumnField(field, visibleFieldKeys)) {
        fieldColumns.push(column)
      } else if (shouldCreateHiddenBehaviorColumn(field, visibleFieldKeys)) {
        hiddenBehaviorColumns.push({
          ...column,
          isFacetable: false,
          facetAccessorFn: undefined,
          facetDisplayFormatter: undefined,
          facetLabel: undefined,
        })
      }
    })

    allColumns.push(...fieldColumns)
    return { columns: allColumns, behaviorColumns: hiddenBehaviorColumns }
  }, [fieldCatalog.fields, fieldProjector, properties, resourceTypeId])

  return {
    columns,
    behaviorColumns,
    loading,
    fieldCatalog,
    fieldProjector,
  }
}
